import { useCallback, useEffect, useState } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchRuns, fetchSummary, jobLabel, JM_STR as S, type JobRun, type JobSummary } from './jobMonApi'

export function JobRunsScreen() {
  const [summary, setSummary] = useState<JobSummary[] | null>(null)
  const [runs, setRuns] = useState<JobRun[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [status, setStatus] = useState('')
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchSummary(ac.signal).then(setSummary).catch(() => setSummary([]))
    fetchRuns(status, ac.signal)
      .then(setRuns)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A17.09"
        description={S.desc}
        actions={
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            <option value="ok">{S.ok}</option>
            <option value="error">{S.error}</option>
          </select>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {/* Per-job summary */}
          <div>
            <p className="mb-2 text-xs font-medium text-secondary">{S.jobs}</p>
            {!summary ? (
              <Skeleton className="h-16 w-full" />
            ) : summary.length === 0 ? (
              <p className="rounded-lg border border-border-default bg-surface-2 px-4 py-3 text-sm text-tertiary">{S.empty}</p>
            ) : (
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {summary.map((j) => (
                  <div key={j.job_name} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${j.last_status === 'error' ? 'bg-danger' : 'bg-success'}`} />
                      <span className="min-w-0 flex-1 truncate text-sm text-primary">{jobLabel(j.job_name)}</span>
                    </div>
                    <p className="mt-1 text-xs text-tertiary">
                      {j.runs} {S.runs}
                      {j.errors > 0 && <span className="text-danger"> · {j.errors} {S.errors}</span>}
                    </p>
                    <p className="text-xs text-tertiary">{S.lastRun}: {new Date(j.last_run).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent runs */}
          <div>
            <p className="mb-2 text-xs font-medium text-secondary">{S.recent}</p>
            {err ? (
              <ErrorState code={err.code} onRetry={reload} />
            ) : !runs ? (
              <div className="space-y-1.5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : runs.length === 0 ? (
              <EmptyState title={S.empty} />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-default">
                <table className="w-full text-left text-sm">
                  <tbody>
                    {runs.map((r, i) => (
                      <tr key={r.id} className={i > 0 ? 'border-t border-border-subtle' : ''}>
                        <td className="px-3 py-2">
                          <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${r.status === 'error' ? 'bg-danger' : 'bg-success'}`} />
                          <span className="text-primary">{jobLabel(r.job_name)}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular text-secondary">{r.items_affected} {S.items}</td>
                        <td className="px-3 py-2 text-right tabular text-tertiary">{r.duration_ms}ms</td>
                        <td className="px-3 py-2 text-right text-xs text-tertiary">{new Date(r.ran_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
